import type { PropertyFilters } from '~/core/entities/property/filters';

import { shortsListingQueryToFilters } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { resolveShortsLocationStatesForLabels } from '~/composables/useShortsFilters/resolveShortsLocationStates';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';

type ShortsEndOfRecommendationQuery = Parameters<
	typeof shortsListingQueryToFilters
>[0];

interface ShortsAutomaticExpansionEligibility {
	hasReachedEndOfRecommendation: boolean;
	hasAppliedFilters: boolean;
	isPdpOrigin: boolean;
	isOpenInventoryFeed: boolean;
}

function queryValueToList(
	value: ShortsEndOfRecommendationQuery[string]
): string[] | undefined {
	const values = (Array.isArray(value) ? value : [value])
		.flatMap(item => (typeof item === 'string' ? item.split(',') : []))
		.map(item => item.trim())
		.filter(Boolean);

	return values.length ? values : undefined;
}

export function shouldShowShortsAutomaticExpansion({
	hasReachedEndOfRecommendation,
	hasAppliedFilters,
	isPdpOrigin,
	isOpenInventoryFeed
}: ShortsAutomaticExpansionEligibility): boolean {
	return (
		hasReachedEndOfRecommendation &&
		(!hasAppliedFilters || isPdpOrigin) &&
		!isOpenInventoryFeed
	);
}

/**
 * Monta a SRP a partir apenas dos filtros de busca compartilhados pelos dois
 * contratos. Contexto de recomendação, ordenação e raio não devem vazar.
 */
export function getShortsSimilarPropertiesPath(
	query: ShortsEndOfRecommendationQuery
): string {
	const filters = shortsListingQueryToFilters(query);
	const features = queryValueToList(query.features);
	const states = resolveShortsLocationStatesForLabels(
		filters.macroregions ?? [],
		filters.states ?? []
	);

	return PropertyFiltersPresenter.getPathFromFilters(
		{
			transactionType: 'sell',
			agentSlug: filters.agentSlug,
			macroregions: filters.macroregions,
			regions: filters.regions,
			cities: filters.cities,
			states,
			propertyTypes: filters.propertyTypes as PropertyFilters['propertyTypes'],
			minAskingPrice: filters.minAskingPrice,
			maxAskingPrice: filters.maxAskingPrice,
			minArea: filters.minArea,
			maxArea: filters.maxArea,
			suites: filters.suites,
			bedrooms: filters.bedrooms,
			parkingSpots: filters.parkingSpots,
			features: features as PropertyFilters['features']
		},
		states.length ? { states } : {}
	);
}
